import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

const ACTION_LABELS: Record<string, string> = {
  MISSING: 'Marcar faltante',
  RECOVERED: 'Marcar recuperado',
  DECEASED: 'Cliente fallecido',
  SKIP_LETTER: 'No enviar carta',
  WHATSAPP: 'Marcar WhatsApp',
  NEXT_COLLECTION: 'Próxima cobranza',
};

export function CollectionWorkstation() {
  const [documentNumber, setDocumentNumber] = useState('');
  const [workstation, setWorkstation] = useState<any | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [noteForm, setNoteForm] = useState({ loanId: '', description: '', type: 'NOVEDAD' });
  const [nextCollectionDate, setNextCollectionDate] = useState('');
  const [sweepForm, setSweepForm] = useState({ branchId: '', overdueDays: '30' });
  const [notesQuery, setNotesQuery] = useState({ from: '', to: '' });
  const [notesList, setNotesList] = useState<any[]>([]);

  useEffect(() => {
    apiService.getBranches(true).then(setBranches).catch(() => {});
  }, []);

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!documentNumber.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCollectionWorkstation(documentNumber.trim());
      setWorkstation(data);
      if (data.loans?.length === 1) {
        setSelectedLoanId(data.loans[0].id);
        setNoteForm((f) => ({ ...f, loanId: data.loans[0].id }));
      }
    } catch (err: any) {
      setWorkstation(null);
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async (loanId: string) => {
    try {
      setTimeline(await apiService.getCollectionTimeline(loanId));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    }
  };

  useEffect(() => {
    if (selectedLoanId) loadTimeline(selectedLoanId);
  }, [selectedLoanId]);

  const refresh = async () => {
    if (documentNumber.trim()) {
      const data = await apiService.getCollectionWorkstation(documentNumber.trim());
      setWorkstation(data);
    }
  };

  const runAction = async (loanId: string, action: string) => {
    setBusy(true);
    setError(null);
    try {
      if (action === 'NEXT_COLLECTION' && !nextCollectionDate) {
        setError('Indique fecha para próxima cobranza');
        return;
      }
      await apiService.applyCollectionAction({
        loanId,
        action,
        nextCollectionAt: action === 'NEXT_COLLECTION' ? nextCollectionDate : undefined,
      });
      await refresh();
      if (selectedLoanId === loanId) await loadTimeline(loanId);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workstation?.customer?.id || !noteForm.loanId || !noteForm.description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.createCollectionNote({
        loanId: noteForm.loanId,
        customerId: workstation.customer.id,
        type: noteForm.type,
        description: noteForm.description,
      });
      setNoteForm((f) => ({ ...f, description: '' }));
      await refresh();
      if (noteForm.loanId) await loadTimeline(noteForm.loanId);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadNotesQuery = async () => {
    if (!workstation?.customer?.id) return;
    setBusy(true);
    try {
      setNotesList(
        await apiService.listCollectionNotes({
          customerId: workstation.customer.id,
          from: notesQuery.from || undefined,
          to: notesQuery.to || undefined,
        }),
      );
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setBusy(false);
    }
  };

  const runNotesSweep = async () => {
    if (!sweepForm.branchId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiService.sweepCollectionNotes({
        branchId: sweepForm.branchId,
        overdueDays: parseInt(sweepForm.overdueDays, 10) || 30,
      });
      alert(`Barrido novedades: ${r.created} notas creadas (${r.loansProcessed} préstamos)`);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setBusy(false);
    }
  };

  const runVerificationsSweep = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await apiService.sweepEmploymentVerifications({
        branchId: sweepForm.branchId || undefined,
        overdueDays: parseInt(sweepForm.overdueDays, 10) || 30,
      });
      alert(`Barrido verificaciones: ${r.created} creadas, ${r.skipped} omitidas`);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setBusy(false);
    }
  };

  const customer = workstation?.customer;

  return (
    <div className="space-y-6">
      <form onSubmit={search} className="flex gap-2 flex-wrap items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">DNI / Nº documento</label>
          <input
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="Ej. 12345678"
            className="px-3 py-2 border border-gray-300 rounded-md w-48"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Buscar cliente'}
        </button>
      </form>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {customer && (
        <>
          <div className="bg-white border rounded-lg p-4 grid md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Cliente</p>
              <p className="font-semibold text-lg">
                {customer.lastName}, {customer.firstName}
              </p>
              <p className="text-sm text-gray-600">DNI {customer.documentNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Contacto</p>
              <p className="text-sm">{customer.phone ?? '—'}</p>
              <p className="text-sm text-gray-600">{customer.city ?? customer.address ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Flags cobranza</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {customer.isDeceased && (
                  <span className="text-xs px-2 py-0.5 bg-gray-800 text-white rounded">Fallecido</span>
                )}
                {customer.whatsappPreferred && (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded">WhatsApp</span>
                )}
                {customer.skipCollectionLetter && (
                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">Sin carta</span>
                )}
                {customer.nextCollectionAt && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                    Próxima: {customer.nextCollectionAt.slice(0, 10)}
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Buró reciente</p>
              <p className="text-sm">
                {(workstation.bureauQueries?.length ?? 0) > 0
                  ? `${workstation.bureauQueries.length} consulta(s)`
                  : 'Sin consultas'}
              </p>
            </div>
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 font-medium">Préstamos activos</div>
            {workstation.loans?.length === 0 ? (
              <p className="p-6 text-gray-500 text-sm">Sin préstamos activos.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Nº</th>
                      <th className="px-4 py-2 text-left">Plan</th>
                      <th className="px-4 py-2 text-left">Estado</th>
                      <th className="px-4 py-2 text-right">Mora</th>
                      <th className="px-4 py-2 text-left">Flags</th>
                      <th className="px-4 py-2 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workstation.loans.map((loan: any) => (
                      <tr
                        key={loan.id}
                        className={`border-t ${selectedLoanId === loan.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-2 font-mono">
                          <button
                            type="button"
                            className="text-blue-700 underline"
                            onClick={() => {
                              setSelectedLoanId(loan.id);
                              setNoteForm((f) => ({ ...f, loanId: loan.id }));
                            }}
                          >
                            #{loan.loanNumber ?? loan.id.slice(0, 8)}
                          </button>
                        </td>
                        <td className="px-4 py-2">{loan.product?.shortName ?? loan.product?.name ?? '—'}</td>
                        <td className="px-4 py-2">{loan.status}</td>
                        <td className="px-4 py-2 text-right text-red-700">
                          {loan.daysOverdue > 0 ? `${loan.daysOverdue} d / ${loan.overdueInstallments} cuotas` : '—'}
                        </td>
                        <td className="px-4 py-2">
                          {loan.collectionMissing && (
                            <span className="text-xs text-red-700 font-medium">Faltante</span>
                          )}
                          {loan.collectionRecoveredAt && (
                            <span className="text-xs text-green-700 ml-1">Recuperado</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(ACTION_LABELS).map((action) => (
                              <button
                                key={action}
                                type="button"
                                disabled={busy}
                                onClick={() => runAction(loan.id, action)}
                                className="text-xs px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                                title={ACTION_LABELS[action]}
                              >
                                {ACTION_LABELS[action].split(' ').slice(-1)[0]}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={submitNote} className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Alta manual de novedad</h3>
              <select
                required
                value={noteForm.loanId}
                onChange={(e) => setNoteForm({ ...noteForm, loanId: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Préstamo…</option>
                {workstation.loans?.map((l: any) => (
                  <option key={l.id} value={l.id}>
                    #{l.loanNumber ?? l.id.slice(0, 8)}
                  </option>
                ))}
              </select>
              <select
                value={noteForm.type}
                onChange={(e) => setNoteForm({ ...noteForm, type: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                {['NOVEDAD', 'LLAMADO', 'VISITA', 'BARRIDO', 'SISTEMA'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <textarea
                required
                rows={3}
                placeholder="Descripción de la novedad"
                value={noteForm.description}
                onChange={(e) => setNoteForm({ ...noteForm, description: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              />
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
              >
                Guardar novedad
              </button>
            </form>

            <div className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Próxima cobranza (acción)</h3>
              <input
                type="date"
                value={nextCollectionDate}
                onChange={(e) => setNextCollectionDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500">
                Seleccione fecha y use el botón &quot;cobranza&quot; en la fila del préstamo.
              </p>

              <h3 className="font-medium pt-2">Consulta novedades</h3>
              <div className="flex gap-2">
                <input type="date" value={notesQuery.from} onChange={(e) => setNotesQuery({ ...notesQuery, from: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                <input type="date" value={notesQuery.to} onChange={(e) => setNotesQuery({ ...notesQuery, to: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                <button type="button" onClick={loadNotesQuery} disabled={busy} className="text-sm px-3 py-1 bg-gray-100 rounded">Filtrar</button>
              </div>
              <ul className="max-h-40 overflow-y-auto text-sm space-y-1">
                {(notesList.length ? notesList : workstation.recentNotes ?? []).map((n: any) => (
                  <li key={n.id} className="border-b pb-1">
                    <span className="text-gray-500">{n.createdAt?.slice?.(0, 10)}</span> [{n.type}] {n.description}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {selectedLoanId && (
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-medium mb-3">Historial / timeline — préstamo seleccionado</h3>
              {timeline.length === 0 ? (
                <p className="text-sm text-gray-500">Sin eventos registrados.</p>
              ) : (
                <ul className="space-y-2 text-sm max-h-64 overflow-y-auto">
                  {timeline.map((ev) => (
                    <li key={`${ev.kind}-${ev.id}`} className="flex gap-3 border-b pb-2">
                      <span className="text-gray-500 whitespace-nowrap">{ev.at?.slice?.(0, 10) ?? String(ev.at).slice(0, 10)}</span>
                      <span className="text-xs uppercase text-blue-700">{ev.kind}</span>
                      <span>{ev.summary}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <div className="bg-white border rounded-lg p-4 space-y-3">
        <h3 className="font-medium">Barridos automáticos</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <select
            value={sweepForm.branchId}
            onChange={(e) => setSweepForm({ ...sweepForm, branchId: e.target.value })}
            className="border rounded-md px-3 py-2"
          >
            <option value="">Sucursal (novedades)…</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            value={sweepForm.overdueDays}
            onChange={(e) => setSweepForm({ ...sweepForm, overdueDays: e.target.value })}
            className="border rounded-md px-3 py-2 w-24"
            placeholder="Días"
          />
          <button type="button" disabled={busy || !sweepForm.branchId} onClick={runNotesSweep} className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm disabled:opacity-50">
            Barrido novedades
          </button>
          <button type="button" disabled={busy} onClick={runVerificationsSweep} className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm disabled:opacity-50">
            Barrido verificaciones
          </button>
        </div>
      </div>
    </div>
  );
}
